"use client";

import React from "react";
import { toggleBookmark, useIsBookmarked } from "@/lib/bookmarks";
import type { IBookmarkedFormation } from "@/types/formation";
import Link from "next/dist/client/link";

/**
 * Props for the TrainingCard component.
 */
interface TrainingCardProps {
    rank?: number;
    rating?: string | number;
    title: string;
    location: string;
    duration: string;
    description: string;
    price: string;
    financing: string;
    category: string;
    isNew?: boolean
    customClass?: string;
    responsive?: boolean
    /** When provided, the bookmark button becomes an interactive save/remove
     *  toggle for this formation. Omitted on demo/placeholder cards, which keep
     *  the button inert. */
    formation?: IBookmarkedFormation;
    onDiscover?: () => void;
    onSubscribe?: () => void;
}

/**
 * TrainingCard Component
 * Refined design with rank-specific colors and top-right rating.
 */
export const TrainingCard: React.FC<TrainingCardProps> = ({
    rank,
    rating,
    title,
    location,
    duration,
    description,
    price,
    financing,
    category,
    isNew = false,
    customClass,
    responsive = false,
    formation,
    onDiscover,
    onSubscribe,
}) => {
    const isSaved = useIsBookmarked(formation?.slug);

    const handleToggleBookmark = () => {
        if (formation) toggleBookmark(formation);
    };

    const categorySlug = formation?.category_slug;

    /* A plain function, not a nested component: defining a component inside
       render remounts it on every pass. */
    const renderCategoryTag = (className: string) =>
        categorySlug ? (
            <Link
                href={`/formations/${categorySlug}`}
                className={`${className} hover:opacity-80 transition-opacity`}
            >
                {category}
            </Link>
        ) : (
            <span className={className}>{category}</span>
        );

    return (
        <div className={`${customClass} h-full flex-1 flex flex-col items-center w-full`}>
            {/* Ranking and Stars Section (Outside the card) */}
            <div className={` ${!rank && !rating ? "hidden" : "flex"}  justify-between flex-row md:flex-col items-center w-full px-5 sm:px-4 mb-[3.5px] sm:mb-2.25`}>
                {rank && (
                    <span className={`font-red-hat font-semibold text-[28px] leading-9.25 md:text-[58px] md:leading-21 ${rank === 1 ? 'text-[#1B365E]' : 'text-[#40BFDE]'}`}>
                        #{rank}
                    </span>
                )}
                {rating && (
                    <div className="flex items-center gap-2.5 sm:-mt-2.5">
                        <span className="font-bold text-[#1B365E] leading-5 font-hanken-grotesk text-[15px]">{rating}</span>
                        <div className="w-6 h-6 flex items-center justify-center">
                            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.38586 0.690968C8.68521 -0.230343 9.98862 -0.230344 10.288 0.690967L11.8066 5.36473C11.9404 5.77675 12.3244 6.05571 12.7576 6.05571H17.6719C18.6406 6.05571 19.0434 7.29533 18.2597 7.86473L14.284 10.7533C13.9335 11.0079 13.7868 11.4593 13.9207 11.8713L15.4393 16.5451C15.7386 17.4664 14.6842 18.2325 13.9004 17.6631L9.9247 14.7746C9.57421 14.5199 9.09962 14.5199 8.74913 14.7746L4.77339 17.6631C3.98968 18.2325 2.9352 17.4664 3.23455 16.5451L4.75314 11.8713C4.88702 11.4593 4.74036 11.0079 4.38987 10.7533L0.414135 7.86473C-0.369579 7.29533 0.0331967 6.05571 1.00192 6.05571H5.9162C6.34943 6.05571 6.73339 5.77675 6.86726 5.36473L8.38586 0.690968Z" fill="#FFBC42" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Card Container */}
            <div className={`w-full h-full bg-[#C7F8FF] rounded-3xl ${responsive ? "p-3 text-center md:text-left md:p-5 md:min-h-75.75" : "p-5 min-h-75.75"} relative flex flex-col flex-1`}>
                {/* Header: Category Tag & Bookmark */}
                <div className={`flex items-start ${responsive ? "mb-2.5 md:mb-2" : "mb-2"} justify-between`}>
                    {renderCategoryTag(`${responsive ? "md:inline-block hidden" : "inline-block"} bg-white text-[#1B515E] text-[8px] leading-5 font-normal px-2.25 py-1 rounded-2xl`)}
                    <span className={`${responsive && isNew ? "md:hidden inline-block" : "invisible"} w-fit text-[8px] leading-2.75 text-[#ABABAB] bg-white px-1 py-0.75 rounded-full`}>
                        Nouveau
                    </span>
                    <button
                        type="button"
                        onClick={formation ? handleToggleBookmark : undefined}
                        aria-pressed={formation ? isSaved : undefined}
                        aria-label={isSaved ? "Retirer des favoris" : "Ajouter aux favoris"}
                        className={`${responsive ? "w-3.5 h-3.5 md:w-6 md:h-6" : "w-6 h-6"} ${isSaved ? "bg-[#FC7F16]" : "bg-white"} rounded-full flex items-center justify-center cursor-pointer transition-colors`}
                    >
                        <svg className={`${responsive && "w-[10.5px] h-[10.5px] md:h-4.5 md:w-4.5"}`} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M9 2.875C7.60513 2.875 6.22936 2.9557 4.87706 3.11264C4.38227 3.17006 4 3.60073 4 4.13024V14.7387L8.72049 12.3785C8.89645 12.2905 9.10355 12.2905 9.27951 12.3785L14 14.7387V4.13024C14 3.60073 13.6177 3.17006 13.1229 3.11264C11.7706 2.9557 10.3949 2.875 9 2.875ZM4.73296 1.87097C6.13299 1.7085 7.55687 1.625 9 1.625C10.4431 1.625 11.867 1.7085 13.267 1.87097C14.4231 2.00514 15.25 2.99777 15.25 4.13024V15.75C15.25 15.9666 15.1378 16.1678 14.9536 16.2817C14.7693 16.3955 14.5392 16.4059 14.3455 16.309L9 13.6363L3.65451 16.309C3.46077 16.4059 3.23068 16.3955 3.04642 16.2817C2.86216 16.1678 2.75 15.9666 2.75 15.75V4.13024C2.75 2.99777 3.57685 2.00514 4.73296 1.87097Z" fill={isSaved ? "#FFFFFF" : "#FC7F16"} />
                        </svg>
                    </button>
                </div>

                <div className={`${responsive ? "min-h-8 flex justify-center" : "hidden"} w-full`}>
                    {renderCategoryTag(`${responsive ? "md:hidden inline-block" : "hidden"} text-center bg-white text-[#1B515E] text-[8px] leading-3.5 font-normal px-2 py-0.5 rounded-full w-fit my-auto`)}
                </div>

                {/* New badge */}
                <span className={`${!responsive && isNew ? "md:inline-block hidden" : "hidden"} w-fit text-[8px] leading-2.75 text-[#1B515E] bg-[#FFE4B4] px-2 py-[4.5px] rounded-full mb-1`}>
                    Nouveau
                </span>

                {/* Course content */}
                <div className="flex-1">
                    <Link href={`/fiche/${formation?.slug ?? ""}`} className="flex items-center gap-2">
                        <h3 className={`text-[#1D1D1D] font-bold ${responsive ? "text-xs leading-3.5 mb-1 mt-3 md:mt-0 md:text-[16px] md:leading-6.5 md:mb-0.75" : "mt-0 text-[16px] leading-6.5 mb-0.75"}`}>
                            {title}
                        </h3>
                    </Link>
                    <p className={`text-[#1B365E] font-normal leading-5 mb-3 ${responsive ? "text-[8px] md:text-[12px]" : "text-[12px]"}`}>{/*location -*/} {duration}</p>
                    <p className={`${responsive ? "md:inline-block hidden" : "inline-block"} text-[#1D1D1D] text-[14px] leading-5 font-normal mb-5`}>
                        {description}
                    </p>

                    <div className="mb-0.5">
                        <p className={`text-[#1B365E] font-bold ${responsive ? "text-xs leading-4.75 mb-4 md:text-[15px] md:leading-4.75 md:mb-1.25" : "text-[15px] leading-4.75 mb-1.25"}`}>{price}</p>
                        {/* <p className={`${responsive ? "md:inline-block hidden" : "inline-block"} text-[#1B365E] text-[8px] leading-5 uppercase font-normal`}>
                            {financing}
                        </p> */}
                    </div>
                </div>

                <div className={`bg-white h-px w-full ${responsive ? "mb-0.5 mt-0 md:my-2" : "my-2"}`} />

                {/* Footer Buttons */}
                <div className={`${responsive ? "flex-col md:flex-row gap-6 md:gap-4" : "gap-4"} pt-2 flex items-center justify-between mt-auto`}>
                    <button
                        onClick={onDiscover}
                        className={`${responsive ? "order-2 md:order-1 mx-auto md:mx-0 text-[8px]! leading-2.5 md:leading-4.5" : "leading-4.5"} flex items-center cursor-pointer justify-center gap-2 text-[#1B365E] font-bold md:text-[15px] hover:opacity-80 transition-opacity`}
                    >
                        <svg className={`${responsive && "w-3 md:w-4 h-3 md:h-4"}`} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z" fill="#1B365E" />
                        </svg>
                        Découvrir
                    </button>
                    <button
                        onClick={onSubscribe}
                        className={`${responsive && "order-1 md:order-2 w-full md:w-fit"} bg-[#1B365E] cursor-pointer border border-[#EFEFEF] text-white font-bold px-5.5 leading-4.5 text-[15px] h-9 rounded-lg hover:bg-white hover:text-[#1B365E] transition-colors`}
                    >
                        S&apos;inscrire
                    </button>
                </div>
            </div>
        </div>
    );
};
